import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Modal, Table } from 'antd';
import { Button, Icon as CroplerIcon, Tag as CroplerTag, Count as CroplerCount } from '@cropler/ui-kit';
import dayjs from 'dayjs';

import styles from './styles.module.scss';
import stylesDropdownMenu from '../../styles/dropdownMenu.module.scss';

import { GetShortId } from './lib/index.jsx';
import { DateLlFormat, DateTimeLlFormat } from '../../constants/data';
import { useViewport } from '../../hooks';
import { BreakpointMobile, BreakpointMobileUp, ModalWidthSmall, PathnamesMap } from '../../constants/ui';
import { DevicesInitialSensorFields } from './constants/index.jsx';
import { SoilSensorConnectModal, SoilSensorInfo } from '../SoilSensor/index.jsx';
import Panel from '../Panel/index.jsx';
import { MoreMenu } from '../PageLayout/index.jsx';
import { DevicesInfo } from './index.jsx';
import MoreMenuItem from '../PageLayout/MoreMenuItem.jsx';

const EditIcon = (props) => <CroplerIcon name="edit" {...props} />;
const InfoIcon = (props) => <CroplerIcon name="info" {...props} />;
const PastIcon = (props) => <CroplerIcon name="past" {...props} />;

const DevicesTable = ({ t, sensorsData, sensors, onClickEdit, onClickAddLocation, onClickEditLocation }) => {
  const { widthViewport } = useViewport();
  const navigate = useNavigate();

  const { currentUserRole } = useSelector((state) => state.auth);

  const [addSoilSensorModalActive, setAddSoilSensorModalActive] = useState(false);
  const [activeSoilSensorInfoPanel, setActiveSoilSensorInfoPanel] = useState(false);
  const [activeInfoPanel, setActiveInfoPanel] = useState(false);
  const [selectedSoilSensorData, setSelectedSoilSensorData] = useState(null);

  const [moreMenuOpened, setMoreMenuOpened] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(DevicesInitialSensorFields);
  const [gps, setGps] = useState([]);
  const [sensorId, setSensorId] = useState('');

  const columns = [
    {
      title: t('devices.table.columns.name'),
      dataIndex: 'sensor',
      key: 'sensor',
      render: (data) => (
        <div className={`${styles.devicesTableSensor}`}>
          <span className={`${styles.devicesTableSensorTitle}`}>
            <strong>{data.name}</strong> {GetShortId(data.deviceId)}
          </span>
          {(currentUserRole === 'editor' || currentUserRole === 'owner') && (
            <Button type="gray" size="small" onClick={() => onClickEdit(data)} icon={<CroplerIcon name="edit" />} />
          )}
        </div>
      ),
      sorter: (a, b) => a.sensor.name.localeCompare(b.sensor.name),
      className: styles.devicesTableColumn,
    },
    {
      title: t('labels.status'),
      dataIndex: 'status',
      key: 'status',
      className: styles.devicesTableColumn,
    },
    {
      title: t('devices.table.columns.lastSync'),
      dataIndex: 'synced',
      key: 'synced',
      render: (data) => (
        <>
          <span className={styles.devicesTableCellLabel}>Last Synced</span>
          <span>{data ? dayjs(data).format(DateTimeLlFormat) : t('empty.noData')}</span>
        </>
      ),
      sorter: (a, b) => dayjs(a.synced, 'DD.MM.YYYY').unix() - dayjs(b.synced, 'DD.MM.YYYY').unix(),
      className: styles.devicesTableColumn,
    },
    {
      title: t('devices.table.columns.field'),
      dataIndex: 'field',
      key: 'field',
      className: styles.devicesTableColumn,
    },
    {
      title: t('devices.table.columns.location'),
      dataIndex: 'location',
      key: 'location',
      className: styles.devicesTableColumn,
    },
    {
      title: t('labels.installedRemoved'),
      dataIndex: 'period',
      key: 'period',
      className: styles.devicesTableColumn,
    },
    {
      title: t('metrics.soilSensor'),
      dataIndex: 'soilSensor',
      key: 'soilSensor',
      className: styles.devicesTableColumn,
    },
    {
      title: '',
      key: 'operation',
      dataIndex: 'operation',
      align: 'right',
      className: styles.devicesTableColumn,
    },
  ];

  const goToPastLocations = (id) => {
    setMoreMenuOpened(false);
    navigate({ pathname: `/${PathnamesMap.devices}/${id}` });
  };

  const closeMoreMenuMobile = () => {
    setMoreMenuOpened(false);
  };

  const openMoreMenuMobile = (sensor) => {
    setSelectedSensor(sensor);
    setMoreMenuOpened(true);
  };

  const onOpenAboutSensor = ({ id, soilSensor, sensor }) => {
    const filteredSensor = sensors.filter((s) => s.id === id);
    if (filteredSensor && filteredSensor.length > 0) {
      setGps(
        filteredSensor[0].gps_point && filteredSensor[0].gps_point.coordinates
          ? filteredSensor[0].gps_point.coordinates
          : [],
      );
    }
    setSensorId(id);
    setMoreMenuOpened(false);
    setSelectedSensor(sensor);

    if (soilSensor) {
      setSelectedSoilSensorData(soilSensor);
      setActiveSoilSensorInfoPanel(true);
    } else {
      setActiveInfoPanel(true);
    }
  };

  const onOpenConnectSoilSensor = (data) => {
    setSensorId(data.id);
    setAddSoilSensorModalActive(true);
  };

  // const getInfoSensorTitle = () => {
  //   if (!sensors || sensors.length === 0) return '';
  //   const filteredSensor = sensors.filter((item) => item.id === sensorId);
  //   return filteredSensor && filteredSensor.length > 0 ? filteredSensor[0].name : '';
  // }

  return (
    <>
      <Table
        className={`${styles.devicesTable} devices-table custom-scroll-small`}
        columns={columns}
        dataSource={sensorsData.map((item) => ({
          key: item.id,
          sensor: { name: item.name, deviceId: item.device_id, id: item.id },
          status: item.location ? (
            <CroplerTag type="success">{t('common.active')}</CroplerTag>
          ) : (
            <CroplerTag type="danger">{t('common.inactive')}</CroplerTag>
          ),
          synced: item.synced_at,
          field: (
            <>
              <span className={styles.devicesTableCellLabel}>{t('devices.table.columns.field')}</span>
              <span className={'nowrap'}>
                {item.location && item.location.field ? item.location.field : t('empty.noField')}
              </span>
            </>
          ),
          location: (
            <>
              <span className={styles.devicesTableCellLabel}>{t('devices.table.currentLocation')}</span>
              <div>
                {item.location ? (
                  item.location.name ? (
                    <strong>{item.location.name}</strong>
                  ) : (
                    t('empty.noData')
                  )
                ) : currentUserRole === 'owner' || currentUserRole === 'editor' ? (
                  <Button
                    className={`${styles.devicesTableButton} ${styles.devicesTableButtonLink}`}
                    type={widthViewport > BreakpointMobile ? 'default' : 'link'}
                    size="small"
                    icon={widthViewport > BreakpointMobile ? <CroplerIcon name="add" /> : null}
                    onClick={() => onClickAddLocation(item)}
                  >
                    {t('devices.table.addLocation')}
                  </Button>
                ) : (
                  t('empty.noData')
                )}
              </div>
            </>
          ),
          period: (
            <>
              <span className={styles.devicesTableCellLabel}>
                {widthViewport < BreakpointMobileUp && !item.location && item.pastLocations.length > 0
                  ? t('devices.table.previousLocationExited')
                  : t('labels.installedRemoved')}
              </span>
              <div>
                {item.location ? (
                  `${dayjs(item.location.install_date).format(DateLlFormat)} — ${dayjs(item.location.removal_date).format(DateLlFormat)}`
                ) : item.pastLocations && item.pastLocations.length > 0 ? (
                  <div className={styles.devicesTableButtonEdit}>
                    {widthViewport > BreakpointMobile && t('locations.prevLocationExited')}
                    <Button type="link" size="small" onClick={() => onClickEditLocation(item.pastLocations[0])}>
                      {t('actions.edit')}
                    </Button>
                  </div>
                ) : (
                  t('empty.noData')
                )}
              </div>
            </>
          ),
          soilSensor: (
            <>
              <span className={styles.devicesTableCellLabel}>{t('metrics.soilSensor')}</span>
              {item.soilSensor && item.soilSensor.meta.status === 'success' && (
                <Button
                  className={`${styles.devicesTableParamLabel} label-parameters`}
                  onClick={() =>
                    onOpenAboutSensor({
                      id: item.id,
                      soilSensor: item.soilSensor,
                      sensor: item,
                    })
                  }
                >
                  {item.soilSensor.params.serial_number}
                </Button>
              )}
              {item.soilSensor && item.soilSensor.meta.status !== 'success' && (
                <span className={`${styles.devicesTableStatus}`}>{item.soilSensor.meta.status}...</span>
              )}
              {!item.soilSensor && (
                <Button
                  className={styles.devicesTableButton}
                  size={'small'}
                  disabled={!item.isSoilSensorAllowed || !item.isSoilSensorSupported}
                  onClick={() => onOpenConnectSoilSensor(item)}
                >
                  {t('actions.connect')}
                </Button>
              )}
            </>
          ),
          operation: (
            <div className={styles.devicesTableNav}>
              {widthViewport > BreakpointMobile ? (
                <Dropdown
                  menu={{
                    items: [
                      item.location &&
                        (currentUserRole === 'owner' || currentUserRole === 'editor') && {
                          key: '1',
                          label: (
                            <Button
                              type="default"
                              className={stylesDropdownMenu.dropdownMenuLink}
                              onClick={() => onClickEditLocation(item.location)}
                            >
                              <CroplerIcon name="edit" />
                              <span>{t('locations.editLocation')}</span>
                            </Button>
                          ),
                        },
                      {
                        key: '2',
                        label: item.pastLocations && item.pastLocations.length > 0 && (
                          <Button
                            type="default"
                            className={stylesDropdownMenu.dropdownMenuLink}
                            onClick={() => goToPastLocations(item.id)}
                          >
                            <CroplerIcon name="past" />
                            <span>{t('locations.pastLocations')}</span>
                            <CroplerCount>{item.pastLocations.length}</CroplerCount>
                          </Button>
                        ),
                      },
                      {
                        key: '3',
                        label: (
                          <Button
                            type="default"
                            className={stylesDropdownMenu.dropdownMenuLink}
                            onClick={() => onOpenAboutSensor({ id: item.id })}
                          >
                            <CroplerIcon name="info" />
                            <span>{t('devices.info')}</span>
                          </Button>
                        ),
                      },
                      item.soilSensor && {
                        key: '4',
                        label: (
                          <Button
                            type="default"
                            className={stylesDropdownMenu.dropdownMenuLink}
                            onClick={() =>
                              onOpenAboutSensor({
                                id: item.id,
                                soilSensor: item.soilSensor,
                              })
                            }
                          >
                            <CroplerIcon name="info" />
                            <span>{t('soil.info')}</span>
                          </Button>
                        ),
                      },
                    ],
                  }}
                >
                  <span>
                    <Button size="small" icon={<CroplerIcon name="more" />} type="gray"></Button>
                  </span>
                </Dropdown>
              ) : (
                <Button
                  size="small"
                  icon={<CroplerIcon name="more" />}
                  type="gray"
                  onClick={() => openMoreMenuMobile(item)}
                ></Button>
              )}
            </div>
          ),
        }))}
        pagination={false}
      />

      {/*More menu mobile*/}
      <Modal open={moreMenuOpened} onCancel={closeMoreMenuMobile} footer={false} centered width={ModalWidthSmall}>
        <MoreMenu>
          {selectedSensor &&
            selectedSensor.location &&
            (currentUserRole === 'owner' || currentUserRole === 'editor') && (
              <MoreMenuItem
                text={t('locations.editLocation')}
                Icon={EditIcon}
                onClickButton={() => onClickEditLocation(selectedSensor.location)}
              />
            )}
          {selectedSensor && selectedSensor.pastLocations && selectedSensor.pastLocations.length > 0 && (
            <MoreMenuItem
              text={t('locations.pastLocations')}
              Icon={PastIcon}
              count={selectedSensor.pastLocations.length}
              onClickButton={() => goToPastLocations(selectedSensor.id)}
            />
          )}
          <MoreMenuItem
            text={t('devices.info')}
            Icon={InfoIcon}
            onClickButton={() => onOpenAboutSensor({ id: selectedSensor.id })}
          />
        </MoreMenu>
      </Modal>

      {activeInfoPanel && (
        <Panel
          title={t('deviceInfo.title')}
          subtitle={selectedSensor ? selectedSensor.name : ''}
          setActivePanel={() => setActiveInfoPanel(false)}
          isFixedView={true}
        >
          <DevicesInfo id={sensorId} gps={gps} t={t} />
        </Panel>
      )}

      {/*Add soil sensor*/}
      <Modal
        open={addSoilSensorModalActive}
        onCancel={() => setAddSoilSensorModalActive(false)}
        className="modalNoPadding mobile-hide-modal"
        footer={false}
        centered
        width={700}
      >
        <SoilSensorConnectModal
          id={sensorId}
          view={'create'}
          onCloseModal={() => setAddSoilSensorModalActive(false)}
          t={t}
        />
      </Modal>

      {/*Soil sensor info*/}
      {activeSoilSensorInfoPanel && (
        <Panel
          title="Soil sensor information"
          subtitle={selectedSensor ? selectedSensor.name : ''}
          setActivePanel={() => setActiveSoilSensorInfoPanel(false)}
          isFixedView={true}
        >
          <SoilSensorInfo
            deviceData={selectedSoilSensorData}
            t={t}
            onClickDisconnect={() => setActiveSoilSensorInfoPanel(false)}
          />
        </Panel>
      )}
    </>
  );
};

export default DevicesTable;
